import { useMemo, useState } from 'react';
import { ACCOUNTS, FEATURED, PICKER_ACCOUNTS, SavingsAccount } from '@/lib/banks';
import { US_STATES } from '@/lib/taxBrackets';
import {
  formatBps,
  formatUsd,
  getHysaAfterTaxBps,
  getReserveQuote,
  getTaxRates,
  gradeRate,
  IncomeBracket,
  INCOME_BRACKETS,
} from '@/lib/yield';

const RESERVE = ACCOUNTS.find((a) => a.isReserve)!;

// FDIC national average savings rate (0.38% as of 2026-08-17, fdic.gov).
const NATIONAL_AVG_SAVINGS_BPS = 38;

// Effective APY: the standing rate, plus the promotional bonus when bonuses are toggled on.
function effApyBps(a: SavingsAccount, bonusOn: boolean): number {
  return a.apyBps + (bonusOn && a.bonusBps ? a.bonusBps : 0);
}

// Typical APY for a competitive high-yield savings account (3.50%).
const HYSA_AVG_BPS = 350;

const BENCHMARKS: Record<string, { label: string; shortLabel: string; sentence: string }> = {
  'national-average': {
    label: 'National Average (typical savings account)',
    shortLabel: 'National average',
    sentence: 'national average',
  },
  'hysa-average': {
    label: 'HYSA Average (typical high-yield account)',
    shortLabel: 'HYSA average',
    sentence: 'HYSA average',
  },
};

// Picker lists institutions alphabetically; the comparison table stays ranked by yield.
const PICKER_NATIONAL = PICKER_ACCOUNTS.filter((a) => !a.region).sort((a, b) =>
  a.institution.localeCompare(b.institution)
);
const PICKER_LOCAL = PICKER_ACCOUNTS.filter((a) => a.region).sort(
  (a, b) => a.region!.localeCompare(b.region!) || a.institution.localeCompare(b.institution)
);

const BENCHMARK_HINTS: Record<string, string> = {
  'national-average': 'FDIC national average across all U.S. savings accounts',
  'hysa-average': 'typical rate for a competitive high-yield savings account',
};

function InsuranceChip({ a }: { a: SavingsAccount }) {
  if (a.insurance === 'FDIC') return <span className="chip fdic" title={a.insuranceNote ?? 'Member FDIC'}>FDIC insured</span>;
  if (a.insurance === 'NCUA') return <span className="chip fdic" title={a.insuranceNote ?? 'Federally insured by NCUA'}>NCUA insured</span>;
  if (a.insurance === 'SIPC') return <span className="chip sipc" title={a.insuranceNote ?? 'SIPC protected'}>SIPC protected</span>;
  return <span className="chip none">Not insured</span>;
}

function TypeChip({ a }: { a: SavingsAccount }) {
  return a.type === 'BANK_ACCOUNT' ? (
    <span className="chip bank">Bank account</span>
  ) : (
    <span className="chip cash">Cash management</span>
  );
}

export default function Home() {
  const [bankId, setBankId] = useState<string>('hysa-average');
  const [manualApy, setManualApy] = useState<string>('');
  const [state, setState] = useState('CA');
  const [bracket, setBracket] = useState<IncomeBracket>('FROM_100K_TO_250K');
  const [bonusOn, setBonusOn] = useState(true);
  const [deposit, setDeposit] = useState(25000);
  const [guardEmail, setGuardEmail] = useState('');
  const [guardStatus, setGuardStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  const profile = { state, bracket };
  const reserve = getReserveQuote(profile, bonusOn);
  const taxes = getTaxRates(profile);

  const selectedBank = PICKER_ACCOUNTS.find((a) => a.id === bankId);
  const selectedBenchmark = BENCHMARKS[bankId];
  const benchmarkApyBps =
    bankId === 'national-average'
      ? NATIONAL_AVG_SAVINGS_BPS
      : bankId === 'hysa-average'
        ? HYSA_AVG_BPS
        : null;
  const userApyBps = useMemo(() => {
    if (bankId === 'manual') {
      const v = parseFloat(manualApy);
      return Number.isFinite(v) && v >= 0 && v <= 20 ? Math.round(v * 100) : null;
    }
    if (benchmarkApyBps !== null) return benchmarkApyBps;
    return selectedBank ? effApyBps(selectedBank, bonusOn) : null;
  }, [bankId, manualApy, selectedBank, benchmarkApyBps, bonusOn]);

  const hasResult = userApyBps !== null;
  const marketBps = PICKER_ACCOUNTS.map((a) => effApyBps(a, bonusOn));
  const verdict = hasResult ? gradeRate(userApyBps!, marketBps) : null;
  const userAfterTax = hasResult ? getHysaAfterTaxBps(userApyBps!, profile) : null;
  const gapBps = hasResult ? reserve.teyBps - userApyBps! : 0;
  const dollarGap = Math.round((deposit * gapBps) / 10000);

  const gradeClass =
    verdict?.grade === 'Excellent'
      ? 'excellent'
      : verdict?.grade === 'Competitive'
        ? 'competitive'
        : verdict?.grade === 'Above average'
          ? 'above'
          : verdict?.grade === 'Below average'
            ? 'below'
            : 'poor';

  // Ranked strictly by yield: tax-equivalent where the tax treatment differs (see disclosure).
  // Reserve holds the top spot only while it earns it.
  const effectiveYieldBps = (a: SavingsAccount) =>
    a.isReserve ? reserve.teyBps : effApyBps(a, bonusOn);
  const sortedFeatured = [...FEATURED].sort((a, b) => effectiveYieldBps(b) - effectiveYieldBps(a));
  const topPickId = sortedFeatured[0]?.id;

  // The POST /api/rate-guard route handler was deleted with the Next server.
  // There is no signup destination until the rate-ingest backend
  // (scripts/rate-audit/API.md) exposes one, so this reports the failure rather
  // than accepting an address and silently dropping it. Point it at the backend
  // when that lands.
  function submitGuard(e: React.FormEvent) {
    e.preventDefault();
    if (!guardEmail.includes('@')) return;
    setGuardStatus('error');
  }

  return (
    <main>
      {/* ---------- hero + checker ---------- */}
      <section className="hero wrap">
        <div className="eyebrow">The honest savings-rate check</div>
        <h1>
          Is your savings rate <em>any good?</em>
        </h1>
        <p className="sub">
          Pick your bank, and in five seconds we&rsquo;ll grade your rate against the whole
          market, including the tax-equivalent yield your bank never shows you.
        </p>

        <div className="checker" id="check">
          <div className="field-grid">
            <div className="field">
              <label htmlFor="bank">Your bank</label>
              <select id="bank" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                <option value="">Select your bank…</option>
                {Object.entries(BENCHMARKS).map(([id, b]) => (
                  <option key={id} value={id}>
                    {b.label}
                  </option>
                ))}
                <optgroup label="National banks & fintechs">
                  {PICKER_NATIONAL.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.institution} · {a.product}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Local & regional, by state">
                  {PICKER_LOCAL.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.region} · {a.institution} · {a.product}
                    </option>
                  ))}
                </optgroup>
                <option value="manual">My bank isn&rsquo;t listed / enter my rate</option>
              </select>
              {selectedBank && (
                <div className="hint num">
                  Current rate: {formatBps(effApyBps(selectedBank, bonusOn))} APY
                  {selectedBank.bonusBps
                    ? bonusOn
                      ? `, includes a +${formatBps(selectedBank.bonusBps)} bonus`
                      : ` standing rate (+${formatBps(selectedBank.bonusBps)} bonus available)`
                    : ''}{' '}
                  (as of {selectedBank.asOf})
                  {!selectedBank.bonusBps && selectedBank.bonusText
                    ? `. Note: ${selectedBank.bonusText.toLowerCase()}.`
                    : ''}
                </div>
              )}
              {selectedBenchmark && benchmarkApyBps !== null && (
                <div className="hint num">
                  {formatBps(benchmarkApyBps)} APY, {BENCHMARK_HINTS[bankId]}
                </div>
              )}
            </div>

            {bankId === 'manual' && (
              <div className="field">
                <label htmlFor="apy">Your current APY (%)</label>
                <input
                  id="apy"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  max="20"
                  placeholder="e.g. 3.80"
                  value={manualApy}
                  onChange={(e) => setManualApy(e.target.value)}
                />
              </div>
            )}

            <div className="field">
              <label htmlFor="deposit">Savings balance</label>
              <input
                id="deposit"
                type="number"
                inputMode="numeric"
                min="0"
                step="1000"
                value={deposit}
                onChange={(e) => setDeposit(Math.max(0, Number(e.target.value)))}
              />
              <div className="hint">Used to show your dollar impact.</div>
            </div>

            <div className="field">
              <label htmlFor="state">State of residence</label>
              <select id="state" value={state} onChange={(e) => setState(e.target.value)}>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="bracket">Household income</label>
              <select
                id="bracket"
                value={bracket}
                onChange={(e) => setBracket(e.target.value as IncomeBracket)}
              >
                {INCOME_BRACKETS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
              <div className="hint">
                We use this to estimate your marginal tax rate. Nothing is stored.
              </div>
            </div>

          </div>

          {/* ---------- verdict ---------- */}
          {hasResult && verdict && (
            <div className="verdict" aria-live="polite">
              <div className="verdict-headline">
                {selectedBenchmark ? `The ${selectedBenchmark.sentence} of` : 'Your'}{' '}
                {formatBps(userApyBps!)} is{' '}
                <span className={`grade ${gradeClass}`}>{verdict.grade.toLowerCase()}</span>.
              </div>
              <p className="verdict-sub num">
                It beats {verdict.percentile}% of the {marketBps.length} savings accounts we track.{' '}
                {gapBps > 0
                  ? `The best tax-equivalent yield available to you is ${formatBps(reserve.teyBps)}.`
                  : `You're already ahead of every account we track. Nice.`}
              </p>

              <div className="duel">
                <div className="duel-card">
                  <div className="who">
                    {bankId === 'manual'
                      ? 'Your account'
                      : selectedBenchmark
                        ? selectedBenchmark.shortLabel
                        : selectedBank?.institution}
                  </div>
                  <div className="big-rate num">{formatBps(userApyBps!)}</div>
                  <div className="rate-caption">APY, fully taxable</div>
                  <div className="rate-rows num">
                    <div>
                      <span>Your marginal tax rate</span>
                      <strong>{(taxes.combinedRate * 100).toFixed(1).replace(/\.0$/, '')}%</strong>
                    </div>
                  </div>
                  <div className="posttax num">
                    <div className="pt-label">
                      <span>Post-tax estimate</span>
                      <em>What you actually keep</em>
                    </div>
                    <strong>{formatBps(userAfterTax!)}</strong>
                  </div>
                </div>

                <div className="duel-card reserve">
                  <div className="who">FutureMoney Reserve</div>
                  <div className="big-rate num">{formatBps(reserve.teyBps)}</div>
                  <div className="rate-caption">
                    Tax-equivalent yield for you: what a normal savings account would have to pay
                    to match it after taxes.
                  </div>
                  <div className="rate-rows num">
                    <div>
                      <span>Base yield</span>
                      <strong>{formatBps(reserve.baseBps)}</strong>
                    </div>
                    <div>
                      <span>IRA match</span>
                      <strong>+0.25%</strong>
                    </div>
                    <div className="boost-row">
                      <span>
                        Welcome boost
                        <button
                          type="button"
                          role="switch"
                          aria-checked={bonusOn}
                          aria-label="Include the welcome boost"
                          className="switch switch-sm"
                          onClick={() => setBonusOn((v) => !v)}
                        />
                      </span>
                      <strong style={bonusOn ? undefined : { color: 'var(--faint)' }}>
                        {bonusOn ? '+0.65%' : '+0.65% off'}
                      </strong>
                    </div>
                    <div>
                      <span>Pre-tax total</span>
                      <strong>{formatBps(reserve.preTaxBps)}</strong>
                    </div>
                    <div>
                      <span>
                        {reserve.symbol === 'SGOV'
                          ? taxes.stateRate === 0
                            ? 'State-tax-exempt (your state has no income tax)'
                            : 'State-tax-exempt (U.S. Treasuries)'
                          : 'Federal-tax-exempt (municipal bonds)'}
                      </span>
                      <strong>✓</strong>
                    </div>
                  </div>
                  <div className="posttax num">
                    <div className="pt-label">
                      <span>Post-tax estimate</span>
                      <em>What you actually keep</em>
                    </div>
                    <strong>
                      {formatBps(
                        Math.round(reserve.teyBps * (1 - taxes.combinedRate))
                      )}
                    </strong>
                  </div>
                </div>
              </div>

              {gapBps > 0 && (
                <div className="impact">
                  <div className="impact-text num">
                    On <strong>{formatUsd(deposit)}</strong>, that gap is worth about{' '}
                    <strong>{formatUsd(dollarGap)}/year</strong> to you.
                  </div>
                  <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Balance:{' '}
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={deposit}
                      onChange={(e) => setDeposit(Math.max(0, Number(e.target.value)))}
                      aria-label="Savings balance"
                    />
                  </label>
                </div>
              )}

              <div className="verdict-ctas">
                <a
                  href="https://futuremoney.co/reserve"
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open a Reserve account →
                </a>
                <a href="#rate-guard" className="btn btn-secondary">
                  Not ready? Watch my rate for free
                </a>
              </div>

              <p className="method-note">
                Estimates assume 2026 single-filer brackets, {US_STATES.find((s) => s.code === state)?.name}{' '}
                residence, and a marginal rate of {Math.round(taxes.federalRate * 100)}% federal
                {taxes.niitRate > 0 ? ' + 3.8% NIIT' : ''} +{' '}
                {(taxes.stateRate * 100).toFixed(1).replace(/\.0$/, '')}% state, after the standard
                deduction. Each income band is priced at one representative income; if you earn
                between roughly $200k and $220k, your exact fund mix may differ.{' '}
                <a href="#how">See the full methodology</a>. Not tax advice.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------- how it works ---------- */}
      <section className="band" id="how">
        <div className="wrap">
          <div className="section-head">
            <h2>Why &ldquo;tax-equivalent yield&rdquo; is the honest number</h2>
            <p>
              Banks advertise APY before taxes. But you keep what&rsquo;s left <em>after</em> taxes,
              and that&rsquo;s where the accounts differ.
            </p>
          </div>
          <div className="explain-grid narrow-none">
            <div className="explain-card">
              <div className="step-k">Savings interest</div>
              <h3>Fully taxable</h3>
              <p>
                Interest from a savings account is ordinary income. Earn 4.00% and pay federal + state
                tax on all of it. In California&rsquo;s middle brackets that can leave you ~2.67%.
              </p>
            </div>
            <div className="explain-card">
              <div className="step-k">Treasury-backed cash</div>
              <h3>State-tax exempt</h3>
              <p>
                Cash management accounts like FutureMoney Reserve can hold U.S. Treasury ETFs (SGOV).
                Treasury interest is exempt from state income tax, so more of the yield survives,
                especially in high-tax states.
              </p>
            </div>
            <div className="explain-card">
              <div className="step-k">Muni-backed cash</div>
              <h3>Federal-tax exempt</h3>
              <p>
                For higher earners, municipal bond ETFs (SUB) pay income exempt from federal tax,
                which is the bigger tax. That&rsquo;s why the fair comparison is tax-equivalent
                yield, not sticker APY.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- rate guard ---------- */}
      <section className="band alt" id="rate-guard">
        <div className="wrap guard-card">
          <div className="eyebrow">Rate Guard · Free</div>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 36px)', marginBottom: 10 }}>
            Banks cut rates quietly. We&rsquo;ll tell you loudly.
          </h2>
          <p style={{ color: 'var(--muted)', maxWidth: 540, margin: '0 auto' }}>
            We re-check every bank&rsquo;s published rate monthly, with screenshots for the record. If
            your bank cuts its rate or falls behind, you get one plain-English email. No spam,
            unsubscribe anytime.
          </p>
          {guardStatus === 'done' ? (
            <p className="guard-ok">You&rsquo;re in. We&rsquo;ll email you if your rate slips. 🎉</p>
          ) : (
            <form className="guard-form" onSubmit={submitGuard}>
              <input
                type="email"
                required
                placeholder="you@email.com"
                value={guardEmail}
                onChange={(e) => setGuardEmail(e.target.value)}
                aria-label="Email address"
              />
              <button className="btn btn-primary" disabled={guardStatus === 'sending'}>
                {guardStatus === 'sending' ? 'Signing you up…' : 'Guard my rate'}
              </button>
            </form>
          )}
          {guardStatus === 'error' && (
            <p style={{ color: 'var(--bad)', marginTop: 10 }}>
              That didn&rsquo;t go through. Please try again.
            </p>
          )}
          <p className="guard-fine">
            {bankId && bankId !== 'manual' && selectedBank
              ? `We'll watch ${selectedBank.institution} for you.`
              : bankId === 'manual' && hasResult
                ? `We'll watch your ${formatBps(userApyBps!)} against the market.`
                : 'Pick your bank above and we’ll watch it specifically.'}
          </p>
        </div>
      </section>

      {/* ---------- comparison table ---------- */}
      <section className="band" id="rates">
        <div className="wrap">
          <div className="section-head">
            <h2>Today&rsquo;s best savings rates</h2>
            <p className="num">
              Ranked by tax-equivalent yield for your profile. Every rate verified against the
              institution&rsquo;s own website; last audit {RESERVE.asOf}.{' '}
              <a href="#disclosure">Advertiser disclosure</a>
            </p>
          </div>
          <div className="table-controls">
            <div>
              <span className="t-label">Include sign-up bonuses</span>
              <span className="t-sub">Applies to every account with a promotional rate.</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={bonusOn}
              aria-label="Include sign-up bonuses"
              className="switch"
              onClick={() => setBonusOn((v) => !v)}
            />
          </div>
          <div className="table-scroll">
            <table className="rates">
              <thead>
                <tr>
                  <th>Institution</th>
                  <th>APY</th>
                  <th>
                    TEY for you<span className="th-note">*</span>
                  </th>
                  <th>Min. to earn</th>
                  <th>Monthly fee</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedFeatured.map((a) => {
                  const isReserve = !!a.isReserve;
                  return (
                    <tr key={a.id} className={isReserve ? 'reserve-row' : undefined}>
                      <td>
                        <div className="inst-row">
                          <img className="bank-logo" src={`/logos/${a.id}.png`} alt="" />
                          <div className="inst">
                            <span className="inst-name">
                              {a.institution}
                              {a.id === topPickId && (
                                <span className="chip featured-chip" style={{ marginLeft: 8 }}>
                                  Top pick
                                </span>
                              )}
                            </span>
                            <span className="inst-product">{a.product}</span>
                            <span className="chips">
                              <TypeChip a={a} />
                              <InsuranceChip a={a} />
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="apy-cell num">
                          {isReserve ? formatBps(reserve.preTaxBps) : formatBps(effApyBps(a, bonusOn))}
                        </span>
                        {!isReserve && a.bonusBps ? (
                          <span className="tey-note num" style={{ color: 'var(--faint)', fontWeight: 500 }}>
                            {bonusOn
                              ? `includes +${formatBps(a.bonusBps)} bonus`
                              : `+${formatBps(a.bonusBps)} bonus available`}
                          </span>
                        ) : null}
                        {a.bonusText && !isReserve && !a.bonusBps && (
                          <span className="tey-note" style={{ color: 'var(--faint)', fontWeight: 500 }}>
                            {a.bonusText}
                          </span>
                        )}
                        {isReserve && !bonusOn && (
                          <span className="tey-note num" style={{ color: 'var(--faint)', fontWeight: 500 }}>
                            +0.65% bonus available
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`apy-cell num${isReserve ? ' tey-win' : ''}`}>
                          {isReserve ? formatBps(reserve.teyBps) : formatBps(effApyBps(a, bonusOn))}
                        </span>
                      </td>
                      <td className="dim">{a.minToEarn > 0 ? formatUsd(a.minToEarn) : '$0'}</td>
                      <td className="dim">{a.monthlyFee > 0 ? formatUsd(a.monthlyFee) : '$0'}</td>
                      <td>
                        <a
                          href={a.url}
                          className={`btn btn-sm ${isReserve ? 'btn-primary' : 'btn-secondary'}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {isReserve ? 'Get started' : 'Visit site'}
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="as-of num">
            *TEY (tax-equivalent yield) uses your state and income inputs above. Savings interest is
            fully taxable, so a bank account&rsquo;s TEY equals its APY. Rates as of {RESERVE.asOf},
            verified monthly with archived screenshots.{' '}
            <a href="/blog">Read the latest rate report</a>.
          </p>
        </div>
      </section>

      {/* ---------- closing CTA ---------- */}
      <section className="band alt">
        <div className="wrap" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 40px)', marginBottom: 12 }}>
            Five minutes now,{' '}
            {formatUsd(
              hasResult && dollarGap > 0
                ? dollarGap
                : Math.max(150, Math.round((deposit * (reserve.teyBps - NATIONAL_AVG_SAVINGS_BPS)) / 10000))
            )}{' '}
            a year from now.
          </h2>
          <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
            Switching to this month&rsquo;s top-yielding account takes about five minutes, and your
            money stays liquid.
          </p>
          <a
            href={sortedFeatured[0].url}
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 17, padding: '14px 32px' }}
          >
            Get started with {sortedFeatured[0].isReserve ? 'Reserve' : sortedFeatured[0].institution} →
          </a>
        </div>
      </section>
    </main>
  );
}
